import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "src/utils/axios";
import buildHubUrl from "src/utils/hubs/buildHubUrl";
import CreateConnection from "src/utils/hubs/HubMatrix";
import { connectToHubUser } from "src/redux/slices/hub";
import { BUILD_ID } from "src/utils/buildInfo";
import { clearAppCache } from "src/utils/clearAppCache";
import { createDeviceCommandRunner, HANDLED_KEY } from "src/utils/deviceCommands";

// Hay un cobro abierto si se ve el «Importe a pagar» del diálogo de cobro.
export const isPaymentOpen = () => !!document.querySelector('[data-testid="payment-amount-due"]');

/** Tope para liberar la sesión antes de limpiar: si el API no responde, se limpia igual. */
export const CLOSE_SESSION_TIMEOUT_MS = 4000;

/**
 * 2026-10-25 · Libera en el servidor la sesión de este dispositivo (lo mismo que
 * «Cerrar sesión» del menú). Sin esto, la limpieza completa mandaba al login pero
 * el dispositivo seguía ocupando un hueco y al entrar decía «no hay sesiones disponibles».
 * Nunca lanza: un fallo o un API lento no impide limpiar.
 */
export async function closeThisDeviceSession(sid, { http = axios, timeoutMs = CLOSE_SESSION_TIMEOUT_MS } = {}) {
  if (!sid || sid === "null" || sid === "undefined") return false;
  let timer;
  try {
    await Promise.race([
      http.post(`/authentication/closeSession/${sid}`),
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error("timeout")), timeoutMs);
      }),
    ]);
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * 2026-10-05 · Admin › Versiones: escucha las órdenes remotas del admin a este
 * dispositivo (`device.command` → `device-command/{restauranteId}`) y las ejecuta.
 * Se monta en el layout principal y en el del TPV (también dentro de una mesa).
 * No pinta nada.
 */
export default function DeviceCommandsBridge() {
  const dispatch = useDispatch();
  const restauranteId = useSelector((s) => s.authJwt?.user?.id);

  useEffect(() => {
    if (!restauranteId) return undefined;

    const runner = createDeviceCommandRunner({
      getLocalSid: () => {
        try {
          return window.localStorage.getItem("sessionIdentifier");
        } catch {
          return null;
        }
      },
      isPaymentOpen,
      sendResult: (commandId, body) => axios.post(`device-commands/${commandId}/result`, body),
      handlers: {
        clearCache: async (args, ctx) => {
          const mode = args && args.mode === "full" ? "full" : "soft";
          // Se confirma antes: tras la limpieza se recarga (y con full ya no hay sesión).
          await ctx.sendResult({ status: "done", result: { mode, build: BUILD_ID } });
          // Completa = cerrar sesión: libera el hueco del dispositivo antes de borrar el token.
          if (mode === "full") await closeThisDeviceSession(window.localStorage.getItem("sessionIdentifier"));
          await clearAppCache(mode, { preserveKeys: [HANDLED_KEY] });
        },
      },
    });

    const cleanup = CreateConnection(
      buildHubUrl("/hub/userhub", { restauranteId }),
      [{ id: `device-command/${restauranteId}`, operator: (env) => runner.handle(env) }],
      dispatch,
      connectToHubUser,
      "user",
    );

    return () => {
      runner.dispose();
      if (typeof cleanup === "function") cleanup();
    };
  }, [restauranteId, dispatch]);

  return null;
}
